<script setup>
import { useForm } from '@inertiajs/vue3';
import Layout from '@/Layouts/TeacherAuthLayout.vue';
import InputError from '@/Components/Form/InputError.vue';
import InputLabel from '@/Components/Form/InputLabel.vue';
import PrimaryButton from '@/Components/Buttons/PrimaryButton.vue';
import TextInput from '@/Components/Form/TextInput.vue';

defineOptions({
    layout: Layout,
});

const props = defineProps({
    profile: {
        type: Object,
        default: () => ({}),
    },
});

const form = useForm({
    is_company: props.profile?.is_company || false,
    name: props.profile?.name || '',
    code: props.profile?.code || '',
    vat_code: props.profile?.vat_code || '',
    address: props.profile?.address || '',
});

const submit = () => form.post(route('auth.teacher.props.store'));
</script>

<template>
    <MetaHead :title="__('page.register.meta_title')" />

    <div>
        <form
            class="flex flex-col gap-4 bg-white rounded-lg p-6"
            @submit.prevent="submit"
        >
            <h2
                class="font-semibold mb-2"
                v-text="__('teacher_step.props')"
            />

            <div>
                <InputLabel :value="__('label.teacher_type')" for="name" />

                <div
                    :class="{
                        'border-[#DEDEE3]': form.is_company,
                        'border-[#2B3811]': !form.is_company,
                    }"
                    class="border rounded-lg px-2 py-1 mt-1 hover:cursor-pointer"
                    @click.prevent="form.is_company = false"
                >
                    <input
                        v-model="form.is_company"
                        :value="false"
                        type="radio"
                        name="is_company"
                        class="border-[#2B3811] text-[#2B3811] shadow-sm focus:ring-[#2B3811]"
                    >
                    <label class="text-sm ml-2 hover:cursor-pointer" v-text="__('teacher_type.individual')"/>
                </div>

                <div
                    :class="{
                        'border-[#DEDEE3]': !form.is_company,
                        'border-[#2B3811]': form.is_company,
                    }"
                    class="border rounded-lg px-2 py-1 mt-2 hover:cursor-pointer"
                    @click.prevent="form.is_company = true"
                >
                    <input
                        v-model="form.is_company"
                        :value="true"
                        type="radio"
                        name="is_company"
                        class="border-[#2B3811] text-[#2B3811] shadow-sm focus:ring-[#2B3811]"
                    >
                    <label class="text-sm ml-2 hover:cursor-pointer" v-text="__('teacher_type.company')"/>
                </div>
            </div>

            <div>
                <InputLabel :value="form.is_company ? __('label.teacher_company_name') : __('label.teacher_individual_name')" for="name" />
                <TextInput
                    id="name"
                    v-model="form.name"
                    type="text"
                    class="mt-1 block w-full"
                    required
                />
                <InputError class="mt-2" :message="form.errors.name" />
            </div>

            <div>
                <InputLabel :value="form.is_company ? __('label.teacher_company_code') : __('label.teacher_individual_code')" for="code" />
                <TextInput
                    id="code"
                    v-model="form.code"
                    type="text"
                    class="mt-1 block w-full"
                    required
                />
                <InputError class="mt-2" :message="form.errors.code" />
            </div>

            <div v-show="form.is_company">
                <InputLabel :value="__('label.teacher_company_vat_code')" for="vat_code" />
                <TextInput
                    id="vat_code"
                    v-model="form.vat_code"
                    type="text"
                    class="mt-1 block w-full"
                />
                <InputError class="mt-2" :message="form.errors.vat_code" />
            </div>

            <div>
                <InputLabel :value="__('label.teacher_address')" for="address" />
                <TextInput
                    id="address"
                    v-model="form.address"
                    type="text"
                    class="mt-1 block w-full"
                    required
                />
                <InputError class="mt-2" :message="form.errors.address" />
            </div>

            <div class="flex justify-end">
                <PrimaryButton
                    :class="{ 'opacity-25': form.processing }"
                    :disabled="form.processing"
                    v-text="__('cta.next')"
                />
            </div>
        </form>
    </div>
</template>
